import { useRegisterCommands } from "../contexts/CommandRegistryContext";
import { HIDDEN, READY, SOON } from "../services/commands/actionHelpers";
import type { CommandAction } from "../services/commands/commands.types";

export interface StoryHeaderCommandsOptions {
  /** The header is showing a story (the story detail path); otherwise both actions are hidden. */
  isStoryPage: boolean;
  /** The header's Share gate: the story is ready and has content. */
  isShareEnabled: boolean;
  /** Opens the header's invite (collaborators) dialog. */
  onShare: () => void;
  /** Mints an embed link and opens the copy-embed-code dialog. */
  onCopyEmbed?: () => void;
  /** Shows "Copy embed code" as a disabled Soon row (a data app has no embed page yet). */
  isCopyEmbedSoon?: boolean;
  isRefreshing: boolean;
  /** The header's "Refresh now" (it reports its own outcome as a toast). */
  onRefresh: () => Promise<void>;
}

/** The header's story actions as commands: share (opens the invite dialog) and refresh the story. */
export const useStoryHeaderCommands = ({ isStoryPage, isShareEnabled, onShare, onCopyEmbed, isCopyEmbedSoon, isRefreshing, onRefresh }: StoryHeaderCommandsOptions): void => {
  const actions: CommandAction[] = [
    {
      id: "story.share", label: "Share the story", group: "story", keywords: ["invite", "collaborate", "people", "access"],
      patterns: ["share", "share story", "invite people", "invite collaborators"],
      isAvailable: () => {
        if (!isStoryPage) return HIDDEN;
        return isShareEnabled ? READY : { status: "blocked", reason: "Available once the story has loaded" };
      },
      run: () => {
        onShare();
        return { status: "done", message: "Opened sharing." };
      },
    },
    {
      id: "story.copyEmbed", label: "Copy embed code", group: "story", keywords: ["embed", "iframe", "snippet", "website", "paste"],
      patterns: ["copy embed code", "embed story", "embed this story"],
      isAvailable: () => {
        if (!isStoryPage || !onCopyEmbed) return HIDDEN;
        if (isCopyEmbedSoon) return SOON;
        return isShareEnabled ? READY : { status: "blocked", reason: "Available once the story has loaded" };
      },
      run: () => {
        onCopyEmbed?.();
        return { status: "done", message: "Opened the embed code." };
      },
    },
    {
      id: "story.refresh", label: "Refresh the story", group: "story", keywords: ["reload", "update", "regenerate", "latest"],
      patterns: ["refresh story", "refresh now", "refresh data"],
      confirm: () => ({ title: "Refresh this story now?", body: "The story is rebuilt from the latest data." }),
      isAvailable: () => {
        if (!isStoryPage) return HIDDEN;
        return isRefreshing ? { status: "blocked", reason: "A refresh is already running" } : READY;
      },
      run: async () => {
        await onRefresh();
        return { status: "done", message: "Asked for a refresh." };
      },
    },
  ];
  useRegisterCommands(actions);
};
